import get from "lodash/get";
import set from "lodash/set";
import React from "react";

import { SIDEBAR_EDIT_CONTENT_KEY } from "cortex/ui/src/fleet/constant/sidebar";
import { editContentGroupAction } from "cortex/ui/src/fleet/redux-store/content-groups/actions";
import { updateUiStrategyAction } from "cortex/ui/src/fleet/redux-store/ui-state/strategy";
import { type Media } from "cortex/ui/src/fleet/view/media-library/model";
import {
  getCreativeCategories,
  normalizeContentGroup,
} from "cortex/ui/src/fleet/view/media-library/utils";
import { SidebarContentEditor } from "cortex/ui/src/fleet/view/sidebar/content-editor/SidebarContentEditor";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import {
  hideFullscreenViewAction,
  showFullscreenViewAction,
} from "cortex/ui/src/redux-store/ui-state/fullscreen-view";
import { editUserContentAction } from "cortex/ui/src/redux-store/user-contents/actions";
import { type InjectedChildProps, Sidebar } from "cortex/ui/src/ui/sidebar";

type CreativeCategoryContentLike = {
  archived?: boolean | null;
  id: string | number;
  tag?: string | null;
};

type StrategyLike = {
  id: string | number;
  strategy?: {
    priorities?: ReadonlyArray<
      ReadonlyArray<{
        conflicts_with?: string | null;
      }>
    >;
  };
};

const getUserContentsAsArray = makeGetAllAsArray<CreativeCategoryContentLike>();
const getStrategiesAsArray = makeGetAllAsArray<StrategyLike>();

type SidebarState = {
  app: {
    strategies?: Parameters<typeof getStrategiesAsArray>[0];
  };
  global: {
    userContents?: Parameters<typeof getUserContentsAsArray>[0];
  };
  pages: {
    strategyDetail: {
      strategy: Parameters<typeof updateUiStrategyAction>[0];
    };
  };
};

function ConnectedSidebarContentEditor({
  data,
  hideSidebar,
  setDrawerActions,
  storePath,
}: InjectedChildProps) {
  const dispatch = useAppDispatch();
  const stateProps = useAppSelector((state) => {
    const typedState = state as SidebarState;
    const { global, app, pages } = typedState;
    const { strategy } = pages.strategyDetail;

    let isStrategy = false;
    let mediaEntry = data as Media;
    if (storePath) {
      mediaEntry = get(typedState, storePath, {}) as Media;
      isStrategy = storePath.includes("strategyDetail");
    }

    const userContents = getUserContentsAsArray(
      global.userContents as Parameters<typeof getUserContentsAsArray>[0],
    );
    const strategies = getStrategiesAsArray(
      app.strategies as Parameters<typeof getStrategiesAsArray>[0],
    );

    const categories = getCreativeCategories(
      userContents as ReadonlyArray<{
        archived?: boolean | null;
        tag?: string | null;
      }>,
      strategies as ReadonlyArray<{
        strategy?: {
          priorities?: ReadonlyArray<
            ReadonlyArray<{ conflicts_with?: string | null }>
          >;
        };
      }>,
    );

    return {
      categories,
      isStrategy,
      mediaEntry,
      strategy,
      storePath,
    };
  });

  const editContent = (content: Media) => {
    dispatch(editUserContentAction(content, "Success! Media edited.") as any);
  };

  const editContentGroup = (contentGroup: Media) => {
    const normalizedContentGroup = normalizeContentGroup(
      contentGroup as Parameters<typeof normalizeContentGroup>[0],
    );
    dispatch(
      editContentGroupAction(
        normalizedContentGroup as Parameters<typeof editContentGroupAction>[0],
        "Success! Media edited.",
      ) as any,
    );
  };

  const strategyEditContent = (content: Media) => {
    const spotPath = stateProps.storePath?.split(
      "pages.strategyDetail.strategy.",
    )[1];

    if (spotPath) {
      set(stateProps.strategy, spotPath, content);
    }

    dispatch(
      updateUiStrategyAction(
        stateProps.strategy as Parameters<typeof updateUiStrategyAction>[0],
      ) as any,
    );
  };

  const showFullscreen = (
    url: string,
    mediaType: "image" | "video" | null,
    filename?: string,
  ) => {
    dispatch(
      showFullscreenViewAction(url, mediaType, filename || "asset") as any,
    );
  };

  const hideFullscreen = () => {
    dispatch(hideFullscreenViewAction as any);
  };

  return (
    <SidebarContentEditor
      categories={stateProps.categories}
      editContent={editContent}
      editContentGroup={editContentGroup}
      hideFullscreen={hideFullscreen}
      hideSidebar={hideSidebar}
      isStrategy={stateProps.isStrategy}
      mediaEntry={stateProps.mediaEntry}
      setDrawerActions={setDrawerActions}
      showFullscreen={showFullscreen}
      strategyEditContent={strategyEditContent}
    />
  );
}

export function FleetSideBar() {
  return (
    <Sidebar>
      <ConnectedSidebarContentEditor eventKey={SIDEBAR_EDIT_CONTENT_KEY} />
    </Sidebar>
  );
}
